import { supabase } from "../supabase-client";

import type {
  Subscription,
  PlanLimits,
  PlanType,
  SubscriptionStatus,
  FeatureType,
} from "../types/database.types";

/**
 * Get active subscription for a user
 */
export async function getUserSubscription(
  userId: string,
): Promise<Subscription | null> {
  // Directly query the subscriptions table instead of using RPC
  // This avoids issues with RPC returning tables vs single records
  const { data: subscription, error: subError } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("user_id", userId)
    .in("status", ["active", "trialing"]) // Include both active and trialing subscriptions
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (subError) {
    console.error("Error getting user subscription:", subError);
    return null;
  }

  if (!subscription) {
    // Self-heal path: if latest row is incomplete, reconcile directly with Stripe.
    // This covers webhook delays/failures and prevents users being stuck on "free".
    const { data: latestSub, error: latestSubError } = await supabase
      .from("subscriptions")
      .select("status, stripe_subscription_id, plan_type")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (
      !latestSubError &&
      latestSub?.status === "incomplete" &&
      latestSub?.stripe_subscription_id
    ) {
      try {
        await supabase.functions.invoke("sync-subscription-status", {
          method: "POST",
          body: {
            userId,
            subscriptionId: latestSub.stripe_subscription_id,
            expectedPlanType: latestSub.plan_type,
          },
        });

        const { data: reconciledSub } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", userId)
          .in("status", ["active", "trialing"])
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (reconciledSub) {
          return reconciledSub as Subscription;
        }
      } catch (reconcileError) {
        console.warn("Subscription reconciliation failed:", reconcileError);
      }
    }

    return null;
  }

  // If trialing and trial_end has passed, convert to free (lazy expiration)
  const sub = subscription as Subscription & { trial_end?: string | null };
  if (sub.status === "trialing" && sub.trial_end) {
    const trialEnd = new Date(sub.trial_end).getTime();
    if (trialEnd < Date.now()) {
      try {
        await supabase.rpc("end_trial_convert_to_free", { p_user_id: userId });
        const { data: updated } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", userId)
          .in("status", ["active", "trialing"])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        return (updated ?? null) as Subscription | null;
      } catch (e) {
        console.warn("Trial conversion failed:", e);
      }
    }
  }

  return subscription as Subscription;
}

/**
 * Get plan limits for a plan type
 */
export async function getPlanLimits(
  planType: PlanType,
): Promise<PlanLimits | null> {
  const { data, error } = await supabase
    .rpc("get_plan_limits", { p_plan_type: planType })
    .single();

  if (error) {
    console.error("Error getting plan limits:", error);
    return null;
  }

  return data as PlanLimits;
}

/**
 * Get current usage for a user and feature
 */
export async function getUserUsage(
  userId: string,
  featureType: FeatureType,
  month?: Date,
): Promise<number> {
  const usageMonth = month
    ? new Date(month.getFullYear(), month.getMonth(), 1)
    : new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const { data, error } = await supabase.rpc("get_user_usage", {
    p_user_id: userId,
    p_feature_type: featureType,
    p_month: usageMonth.toISOString().split("T")[0],
  });

  if (error) {
    console.error("Error getting user usage:", error);
    return 0;
  }

  return (data as number) || 0;
}

/**
 * Increment usage for a user and feature
 * Automatically uses credits if plan limit is reached
 */
export async function incrementUsage(
  userId: string,
  featureType: FeatureType,
  amount: number = 1,
): Promise<{ newCount: number; usedCredits: boolean; creditsUsed?: number }> {
  // Check if we need to use credits
  const subscription = await getUserSubscription(userId);
  let usedCredits = false;
  let creditsUsed = 0;

  if (subscription) {
    const limits = await getPlanLimits(subscription.plan_type);
    const currentUsage = await getUserUsage(userId, featureType);

    if (limits) {
      let limitValue: number | null = null;
      switch (featureType) {
        case "document_generation":
          limitValue = limits.documents_per_month;
          break;
        case "contract_generation":
          limitValue = limits.contract_generations_per_month ?? null;
          break;
        case "consultation":
          limitValue = limits.consultations_per_month;
          break;
        case "case":
          limitValue = limits.cases_per_month;
          break;
        case "exercise":
          limitValue = limits.exercises_per_month;
          break;
        case "exam":
          limitValue = limits.exams_per_month;
          break;
        case "simulation":
          limitValue = limits.simulations_per_month;
          break;
        case "document_review":
          limitValue = limits.document_reviews_per_month;
          break;
        case "case_summary":
          limitValue = limits.case_summaries_per_month;
          break;
        case "legal_ai_chat":
          limitValue = limits.legal_ai_queries_per_month ?? null;
          break;
      }

      // If limit is reached or will be reached, use credits
      if (limitValue !== null && currentUsage >= limitValue) {
        // Import credits functions dynamically
        const { deductCredits, getFeatureCreditsCost } =
          await import("./credits");
        const creditsCost = getFeatureCreditsCost(featureType);

        try {
          await deductCredits(userId, creditsCost, featureType);
          usedCredits = true;
          creditsUsed = creditsCost;
        } catch (error) {
          console.error("Error using credits:", error);
          // If credits fail, still increment usage (the limit check should have prevented this)
          // but we'll let it through for now
        }
      }
    }
  }

  // Increment usage tracking
  const { data, error } = await supabase.rpc("increment_usage", {
    p_user_id: userId,
    p_feature_type: featureType,
    p_amount: amount,
  });

  if (error) {
    console.error("Error incrementing usage:", error);
    throw error;
  }

  return {
    newCount: (data as number) || 0,
    usedCredits,
    creditsUsed: usedCredits ? creditsUsed : undefined,
  };
}

/**
 * Check if user can perform action (check limit)
 */
export async function checkFeatureLimit(
  userId: string,
  featureType: FeatureType,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("check_feature_limit", {
    p_user_id: userId,
    p_feature_type: featureType,
  });

  if (error) {
    console.error("Error checking feature limit:", error);
    return false;
  }

  return (data as boolean) || false;
}

/**
 * Get remaining quota for a feature
 */
export async function getRemainingQuota(
  userId: string,
  featureType: FeatureType,
): Promise<number | null> {
  const subscription = await getUserSubscription(userId);
  if (!subscription) {
    return null;
  }

  const limits = await getPlanLimits(subscription.plan_type);
  if (!limits) {
    return null;
  }

  let limitValue: number | null = null;
  switch (featureType) {
    case "document_generation":
      limitValue = limits.documents_per_month;
      break;
    case "contract_generation":
      limitValue = limits.contract_generations_per_month ?? null;
      break;
    case "consultation":
      limitValue = limits.consultations_per_month;
      break;
    case "case":
      limitValue = limits.cases_per_month;
      break;
    case "exercise":
      limitValue = limits.exercises_per_month;
      break;
    case "exam":
      limitValue = limits.exams_per_month;
      break;
    case "simulation":
      limitValue = limits.simulations_per_month;
      break;
    case "document_review":
      limitValue = limits.document_reviews_per_month;
      break;
    case "case_summary":
      limitValue = limits.case_summaries_per_month;
      break;
    case "legal_ai_chat":
      limitValue = limits.legal_ai_queries_per_month ?? null;
      break;
  }

  if (limitValue === null) {
    return null; // Unlimited
  }

  const currentUsage = await getUserUsage(userId, featureType);
  return Math.max(0, limitValue - currentUsage);
}

/**
 * Create or update subscription
 */
export async function upsertSubscription(
  subscription: Partial<Subscription> & {
    user_id: string;
    plan_type: PlanType;
    current_period_end: string;
    plan_tier: string;
    user_type: string;
  },
): Promise<Subscription> {
  const { data, error } = await supabase
    .from("subscriptions")
    .upsert(subscription, {
      onConflict: "user_id",
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    console.error("Error upserting subscription:", error);
    throw error;
  }

  return data as Subscription;
}

/**
 * Update subscription status
 */
export async function updateSubscriptionStatus(
  subscriptionId: string,
  status: SubscriptionStatus,
): Promise<Subscription> {
  const { data, error } = await supabase
    .from("subscriptions")
    .update({
      status,
      updated_at: new Date().toISOString(),
    })
    .eq("id", subscriptionId)
    .select()
    .single();

  if (error) {
    console.error("Error updating subscription status:", error);
    throw error;
  }

  return data as Subscription;
}

/**
 * Cancel subscription
 */
export async function cancelSubscription(
  subscriptionId: string,
  cancelAtPeriodEnd: boolean = true,
): Promise<Subscription> {
  // Use backend-managed cancellation to keep Stripe and DB in sync.
  const { data, error } = await supabase.functions.invoke(
    "manage-subscription",
    {
      method: "POST",
      body: {
        action: cancelAtPeriodEnd ? "cancel_at_period_end" : "cancel_now",
        subscriptionId,
      },
    },
  );

  if (error) {
    console.error("Error canceling subscription:", error);
    throw error;
  }

  return data as Subscription;
}

/**
 * Reactivate a subscription that was set to cancel at period end
 */
export async function reactivateSubscription(
  subscriptionId: string,
): Promise<Subscription> {
  const { data, error } = await supabase.functions.invoke(
    "manage-subscription",
    {
      method: "POST",
      body: {
        action: "reactivate",
        subscriptionId,
      },
    },
  );

  if (error) {
    console.error("Error reactivating subscription:", error);
    throw error;
  }

  return data as Subscription;
}

/**
 * Get all usage for a user in current month
 */
export async function getUserUsageSummary(
  userId: string,
): Promise<Record<string, number>> {
  const currentMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
  );
  return getUsageSummaryForMonth(userId, currentMonth);
}

/**
 * Get usage summary for a user in a specific month (for billing reports)
 */
export async function getUsageSummaryForMonth(
  userId: string,
  month: Date,
): Promise<Record<string, number>> {
  const monthStr = new Date(month.getFullYear(), month.getMonth(), 1)
    .toISOString()
    .split("T")[0];

  const { data, error } = await supabase
    .from("usage_tracking")
    .select("feature_type, usage_count")
    .eq("user_id", userId)
    .eq("usage_month", monthStr);

  if (error) {
    console.error("Error getting usage summary for month:", error);
    return {};
  }

  const summary: Record<string, number> = {};
  (data || []).forEach((item) => {
    summary[item.feature_type] = item.usage_count;
  });

  return summary;
}

/**
 * Create a Stripe Customer Portal session
 * Passes return_url so billing portal redirects back to current origin (works with localhost)
 */
export async function createPortalSession(): Promise<string> {
  const returnUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/dashboard/settings`
      : undefined;
  const { data, error } = await supabase.functions.invoke(
    "create-portal-session",
    {
      method: "POST",
      body: returnUrl ? { return_url: returnUrl } : {},
    },
  );

  if (error) {
    console.error("Error creating portal session:", error);
    throw error;
  }

  if (!data?.url) {
    throw new Error("No portal URL returned from server");
  }

  return data.url;
}
